import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useAppContext } from '../AppContext';

const WinnerTicker = () => {
    const { t } = useTranslation();
    const { db, appId } = useAppContext();
    const [winner, setWinner] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!appId) return;

        const lotteryRef = doc(db, `artifacts/${appId}/public/data/lottery/current`);
        const unsubscribe = onSnapshot(lotteryRef, (doc) => {
            const data = doc.data();
            const lastWinner = data?.lastWinner;

            if (lastWinner && lastWinner.drawnAt) {
                const drawTime = lastWinner.drawnAt.toDate();
                const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
                
                if (drawTime > oneHourAgo) {
                    const dismissed = sessionStorage.getItem(`lotteryWinnerDismissed_${lastWinner.id}_${drawTime.getTime()}`);
                    if (!dismissed) {
                        setWinner(lastWinner);
                        setIsVisible(true);
                    }
                } else {
                    setIsVisible(false);
                }
            } else {
                setIsVisible(false);
            }
        });

        return () => unsubscribe();
    }, [db, appId]);

    const handleDismiss = () => {
        if (winner) {
            sessionStorage.setItem(`lotteryWinnerDismissed_${winner.id}_${winner.drawnAt.toDate().getTime()}`, 'true');
        }
        setIsVisible(false);
    };

    const renderWinnerMessage = () => {
        if (!winner) return null;

        if (winner.type === 'syndicate' && winner.winningMember) {
            return (
                <Trans i18nKey="winnerTicker.congratulationsSyndicate"
                    values={{ 
                        syndicateName: winner.displayName, 
                        memberName: winner.winningMember.displayName,
                        prize: winner.prize.toLocaleString() 
                    }}
                    components={{ 1: <span className="font-bold text-yellow-300" />, 2: <span className="font-bold text-cyan-300" />, 3: <span className="font-bold text-green-400" /> }}
                />
            );
        }

        return (
            <Trans i18nKey="winnerTicker.congratulationsIndividual"
                values={{ 
                    displayName: winner.displayName, 
                    prize: winner.prize.toLocaleString() 
                }}
                components={{ 1: <span className="font-bold text-yellow-300" />, 2: <span className="font-bold text-green-400" /> }}
            />
        );
    };

    return (
        <AnimatePresence>
            {isVisible && winner && (
                <motion.div 
                    className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-800 via-gray-900 to-purple-800 border-t-2 border-yellow-400 shadow-lg h-14 flex items-center z-50 overflow-hidden"
                    initial={{ y: 100 }}
                    animate={{ y: 0 }}
                    exit={{ y: 100 }}
                >
                    <div className="absolute flex items-center animate-ticker-scroll whitespace-nowrap">
                        {Array(5).fill(0).map((_, i) => (
                            <p key={i} className="text-lg text-white px-12">
                                {renderWinnerMessage()}
                            </p>
                        ))}
                    </div>
                    <button onClick={handleDismiss} className="absolute top-1 right-1 text-gray-400 hover:text-white z-10">
                        <LucideIcons.X size={16} />
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default WinnerTicker;